import React from 'react';
import { GLOBAL_UTILS } from '../../styles/globalStyles';
import { quranTrackingTranslations } from '../../locales';
import { getEvalBadgeStyle } from '../../utils/evaluationUtils';

interface QuranRecordSectionProps {
  latestRecord: {
    id?: number;
    studentId?: number; // ✅ NOUVEAU - ID de l'étudiant (nécessaire pour mettre à jour quran_next_session)
    
    // Dates (DTO camelCase - selon guide backend)
    modifiedDate?: string; // Format: "YYYY-MM-DD HH:mm:ss"
    createdDate?: string;
    
    // Évaluations
    evalNewHifz?: 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'; // تقييم الحفظ الجديد
    evalOldHifzMurajah?: 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'; // تقييم مراجعة الحفظ القديم
    evalNewHifzMurajah?: 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'; // تقييم مراجعة الحفظ القريب
    
    // Compteur de répétitions
    repeatNewHifz?: number; // ✅ NOUVEAU
    
    // Nouveau Hifz (mémorisation nouvelle)
    newHifzTxt?: string;
    newHifzRange?: string;
    
    // Ancien Hifz - Murajaah (révision ancien)
    oldHifzMurajahTxt?: string;
    oldHifzMurajahRange?: string;
    
    // Nouveau Hifz - Murajaah (révision du nouveau hifz) - NOUVEAU
    newHifzMurajahText?: string;
    newHifzMurajahRange?: string;
    
    // Prochaine Halaqa - Nouveau Hifz (devoir/assignment)
    nextHalaqaNewHifzText?: string;
    nextHalaqaNewHifzRange?: string;
    
    // Prochaine Halaqa - Ancien Hifz Murajaah - NOUVEAU
    nextHalaqaOldHifzMurajahText?: string;
    nextHalaqaOldHifzMurajahRange?: string;
    
    // Prochaine Halaqa - Nouveau Hifz Murajaah - NOUVEAU
    nextHalaqaNewHifzMurajahText?: string;
    nextHalaqaNewHifzMurajahRange?: string;
    
    // Note
    note?: string;
  };
  language?: 'ar' | 'fr' | 'en'; // Langue pour les traductions
  cleanText?: boolean;
  onEdit?: () => void;
  canEdit?: boolean;
  mapEnglishToArabicEval?: (englishValue: string, type?: 'newMemorization' | 'review') => string;
  extractOriginalNoteText?: (note: string) => string;
  windowWidth?: number;
  targetRepeatNewHifzCount?: number; // ✅ NOUVEAU - Paramètre pour le badge de répétition (objectif)
  onUpdateRepeatNewHifz?: (studentId: number, newValue: number) => Promise<void>; // ✅ CORRIGÉ - Utilise studentId au lieu de recordId
  lastPreviousRecord?: {
    newHifzTxt?: string;
    repeatNewHifz?: number;
    _key?: string; // Clé pour la stabilité de la comparaison dans React.memo
  }; // ✅ NOUVEAU - Dernier enregistrement de الجلسات السابقة pour afficher تكرار آخر حفظ
  onUpdateEvaluations?: (recordId: number, evaluations: { evalNewHifz?: string; evalOldHifzMurajah?: string; evalNewHifzMurajah?: string }) => Promise<void>; // ✅ NOUVEAU - Callback pour mettre à jour les évaluations
}

/**
 * Composant réutilisable pour afficher les détails d'une session de Coran
 * Utilisé dans ParentDashboard et TeacherDashboard pour éviter la duplication
 */
const QuranRecordSectionComponent: React.FC<QuranRecordSectionProps> = ({
  latestRecord,
  language = 'ar', // Langue par défaut: arabe
  cleanText = false,
  onEdit,
  canEdit = false,
  mapEnglishToArabicEval,
  extractOriginalNoteText,
  windowWidth = 1024,
  targetRepeatNewHifzCount = 15, // ✅ NOUVEAU - Valeur par défaut: 15
  onUpdateRepeatNewHifz, // ✅ NOUVEAU - Callback pour mettre à jour
  lastPreviousRecord, // ✅ NOUVEAU - Dernier enregistrement de الجلسات السابقة
  onUpdateEvaluations // ✅ NOUVEAU - Callback pour mettre à jour les évaluations
}) => {
  // État local pour la valeur de répétition (pour les boutons +/-)
  // IMPORTANT: Les hooks doivent être appelés avant tout return conditionnel
  // ✅ CORRIGÉ: Utiliser nextSessionRepeatNewHifz au lieu de repeatNewHifz pour la session suivante
  const [localRepeatNewHifz, setLocalRepeatNewHifz] = React.useState<number>((latestRecord as any)?.nextSessionRepeatNewHifz ?? 0);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [lastSavedValue, setLastSavedValue] = React.useState<number>((latestRecord as any)?.nextSessionRepeatNewHifz ?? 0);
  
  // ✅ NOUVEAU - État local pour les évaluations
  const [localEvalNewHifz, setLocalEvalNewHifz] = React.useState<'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'>(latestRecord?.evalNewHifz || 'NotDone');
  const [localEvalOldHifzMurajah, setLocalEvalOldHifzMurajah] = React.useState<'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'>(latestRecord?.evalOldHifzMurajah || 'NotDone');
  const [localEvalNewHifzMurajah, setLocalEvalNewHifzMurajah] = React.useState<'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone'>(latestRecord?.evalNewHifzMurajah || 'NotDone');
  const [isSavingEvaluations, setIsSavingEvaluations] = React.useState<boolean>(false);
  const [lastSavedEvaluations, setLastSavedEvaluations] = React.useState<{
    evalNewHifz: string;
    evalOldHifzMurajah: string;
    evalNewHifzMurajah: string;
  }>({
    evalNewHifz: latestRecord?.evalNewHifz || 'NotDone',
    evalOldHifzMurajah: latestRecord?.evalOldHifzMurajah || 'NotDone',
    evalNewHifzMurajah: latestRecord?.evalNewHifzMurajah || 'NotDone'
  });
  
  // Mettre à jour l'état local quand latestRecord change
  React.useEffect(() => {
    if (latestRecord) {
      // ✅ CORRIGÉ: Calculer nextSessionRepeatNewHifz à l'intérieur du useEffect pour qu'il se mette à jour
      const nextSessionRepeatNewHifz = (latestRecord as any)?.nextSessionRepeatNewHifz ?? 0;
      const newValue = nextSessionRepeatNewHifz;
      
      console.log('🔍 QuranRecordSection - useEffect repeatNewHifz:', {
        newValue,
        lastSavedValue,
        localRepeatNewHifz,
        willUpdate: newValue !== lastSavedValue,
        latestRecordId: latestRecord.id,
        nextSessionRepeatNewHifz: (latestRecord as any)?.nextSessionRepeatNewHifz
      });
      
      // ✅ Mettre à jour repeatNewHifz seulement si la valeur du backend est différente de lastSavedValue
      // ET si newValue >= lastSavedValue (ne jamais régresser - évite les problèmes de timing avec les refreshes multiples)
      // Cela permet de synchroniser avec les données du backend après un refresh
      // tout en permettant les modifications locales
      if (newValue !== lastSavedValue && newValue >= lastSavedValue) {
        console.log('✅ QuranRecordSection - Mise à jour de repeatNewHifz:', {
          from: lastSavedValue,
          to: newValue
        });
        setLocalRepeatNewHifz(newValue);
        setLastSavedValue(newValue);
      } else if (newValue < lastSavedValue) {
        console.log('⚠️ QuranRecordSection - Valeur backend inférieure ignorée (refresh trop tôt):', {
          newValue,
          lastSavedValue,
          info: 'Attente du prochain refresh avec la valeur correcte'
        });
      }
      
      // ✅ Mettre à jour les évaluations SEULEMENT si elles ont changé dans latestRecord
      const newEvalNewHifz = latestRecord.evalNewHifz || 'NotDone';
      const newEvalOldHifzMurajah = latestRecord.evalOldHifzMurajah || 'NotDone';
      const newEvalNewHifzMurajah = latestRecord.evalNewHifzMurajah || 'NotDone';
      
      // Ne réinitialiser que si les valeurs du record sont différentes des valeurs sauvegardées
      // Cela permet de garder les modifications locales même si le record est re-rendu
      if (
        newEvalNewHifz !== lastSavedEvaluations.evalNewHifz ||
        newEvalOldHifzMurajah !== lastSavedEvaluations.evalOldHifzMurajah ||
        newEvalNewHifzMurajah !== lastSavedEvaluations.evalNewHifzMurajah
      ) {
        setLocalEvalNewHifz(newEvalNewHifz);
        setLocalEvalOldHifzMurajah(newEvalOldHifzMurajah);
        setLocalEvalNewHifzMurajah(newEvalNewHifzMurajah);
        setLastSavedEvaluations({
          evalNewHifz: newEvalNewHifz,
          evalOldHifzMurajah: newEvalOldHifzMurajah,
          evalNewHifzMurajah: newEvalNewHifzMurajah
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestRecord, lastSavedValue, lastSavedEvaluations]);

  // Debug: vérifier si onUpdateRepeatNewHifz est défini
  React.useEffect(() => {
    if (latestRecord?.newHifzTxt) {
      console.log('🔍 QuranRecordSection - Debug bouton [ + تكرار الحفظ - ]:', {
        hasOnUpdateRepeatNewHifz: !!onUpdateRepeatNewHifz,
        isFunction: typeof onUpdateRepeatNewHifz === 'function',
        onUpdateRepeatNewHifz: onUpdateRepeatNewHifz,
        latestRecordId: latestRecord.id,
        latestRecordStudentId: latestRecord.studentId, // ✅ CORRIGÉ - Nécessaire pour la nouvelle route
        newHifzTxt: latestRecord.newHifzTxt,
        shouldShowButton: typeof onUpdateRepeatNewHifz === 'function'
      });
    }
  }, [onUpdateRepeatNewHifz, latestRecord?.id, latestRecord?.studentId, latestRecord?.newHifzTxt]);

  // ✅ NOUVEAU : Handlers pour gérer la logique "Absent"
  // Si un champ passe à "Absent", tous passent à "Absent"
  // Si un champ sort de "Absent", les autres "Absent" passent à "NotDone"
  const handleEvalNewHifzChange = React.useCallback((newValue: 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone') => {
    const oldValue = localEvalNewHifz;
    
    // Règle 1 : Si on passe à "Absent", propager aux autres
    if (newValue === 'Absent') {
      setLocalEvalNewHifz('Absent');
      setLocalEvalOldHifzMurajah('Absent');
      setLocalEvalNewHifzMurajah('Absent');
    }
    // Règle 2 : Si on sort de "Absent", les autres "Absent" passent à "NotDone"
    else if (oldValue === 'Absent') {
      setLocalEvalNewHifz(newValue);
      if (localEvalOldHifzMurajah === 'Absent') {
        setLocalEvalOldHifzMurajah('NotDone');
      }
      if (localEvalNewHifzMurajah === 'Absent') {
        setLocalEvalNewHifzMurajah('NotDone');
      }
    }
    // Cas normal : changement simple
    else {
      setLocalEvalNewHifz(newValue);
    }
  }, [localEvalNewHifz, localEvalOldHifzMurajah, localEvalNewHifzMurajah]);

  const handleEvalOldHifzMurajahChange = React.useCallback((newValue: 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone') => {
    const oldValue = localEvalOldHifzMurajah;
    
    // Règle 1 : Si on passe à "Absent", propager aux autres
    if (newValue === 'Absent') {
      setLocalEvalNewHifz('Absent');
      setLocalEvalOldHifzMurajah('Absent');
      setLocalEvalNewHifzMurajah('Absent');
    }
    // Règle 2 : Si on sort de "Absent", les autres "Absent" passent à "NotDone"
    else if (oldValue === 'Absent') {
      setLocalEvalOldHifzMurajah(newValue);
      if (localEvalNewHifz === 'Absent') {
        setLocalEvalNewHifz('NotDone');
      }
      if (localEvalNewHifzMurajah === 'Absent') {
        setLocalEvalNewHifzMurajah('NotDone');
      }
    }
    // Cas normal : changement simple
    else {
      setLocalEvalOldHifzMurajah(newValue);
    }
  }, [localEvalNewHifz, localEvalOldHifzMurajah, localEvalNewHifzMurajah]);

  const handleEvalNewHifzMurajahChange = React.useCallback((newValue: 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone') => {
    const oldValue = localEvalNewHifzMurajah;
    
    // Règle 1 : Si on passe à "Absent", propager aux autres
    if (newValue === 'Absent') {
      setLocalEvalNewHifz('Absent');
      setLocalEvalOldHifzMurajah('Absent');
      setLocalEvalNewHifzMurajah('Absent');
    }
    // Règle 2 : Si on sort de "Absent", les autres "Absent" passent à "NotDone"
    else if (oldValue === 'Absent') {
      setLocalEvalNewHifzMurajah(newValue);
      if (localEvalNewHifz === 'Absent') {
        setLocalEvalNewHifz('NotDone');
      }
      if (localEvalOldHifzMurajah === 'Absent') {
        setLocalEvalOldHifzMurajah('NotDone');
      }
    }
    // Cas normal : changement simple
    else {
      setLocalEvalNewHifzMurajah(newValue);
    }
  }, [localEvalNewHifz, localEvalOldHifzMurajah, localEvalNewHifzMurajah]);

  // ✅ NOUVEAU : Réinitialiser repeatNewHifz à 0 si l'évaluation passe d'une valeur positive à non positive
  React.useEffect(() => {
    const isPositiveEval = localEvalNewHifz === 'Excellent' || localEvalNewHifz === 'Good' || localEvalNewHifz === 'Moyen';
    
    // Si l'évaluation n'est plus positive ET que repeatNewHifz n'est pas déjà 0
    if (!isPositiveEval && localRepeatNewHifz > 0 && latestRecord.studentId && onUpdateRepeatNewHifz) {
      console.log('⚠️ QuranRecordSection - Évaluation non positive détectée, réinitialisation de repeatNewHifz à 0:', {
        localEvalNewHifz,
        localRepeatNewHifz,
        studentId: latestRecord.studentId
      });
      
      // Réinitialiser localement
      setLocalRepeatNewHifz(0);
      setLastSavedValue(0);
      
      // Réinitialiser dans la base de données (de manière asynchrone)
      onUpdateRepeatNewHifz(latestRecord.studentId, 0).catch(error => {
        console.error('❌ Erreur lors de la réinitialisation de repeatNewHifz:', error);
      });
    }
  }, [localEvalNewHifz, localRepeatNewHifz, latestRecord.studentId, onUpdateRepeatNewHifz]);

  if (!latestRecord) return null;

  // Fonction pour gérer l'augmentation
  const handleIncrease = () => {
    setLocalRepeatNewHifz(prev => prev + 1);
  };

  // Fonction pour gérer la diminution
  const handleDecrease = () => {
    setLocalRepeatNewHifz(prev => Math.max(0, prev - 1));
  };

  // Fonction pour sauvegarder
  const handleSave = async () => {
    if (!latestRecord.studentId || !onUpdateRepeatNewHifz) return;
    
    const valueToSave = localRepeatNewHifz;
    console.log('💾 QuranRecordSection - handleSave - Début:', {
      valueToSave,
      studentId: latestRecord.studentId,
      currentLastSavedValue: lastSavedValue
    });
    
    setIsSaving(true);
    try {
      await onUpdateRepeatNewHifz(latestRecord.studentId, valueToSave);
      // ✅ Mettre à jour lastSavedValue pour éviter que le useEffect ne réinitialise la valeur
      // Le useEffect ne se déclenchera que si la valeur du backend est différente de celle-ci
      console.log('✅ QuranRecordSection - handleSave - Succès, mise à jour lastSavedValue:', valueToSave);
      setLastSavedValue(valueToSave);
    } catch (error) {
      console.error('❌ QuranRecordSection - handleSave - Erreur:', error);
      // Revenir à l'ancienne valeur en cas d'erreur
      setLocalRepeatNewHifz((latestRecord as any)?.nextSessionRepeatNewHifz ?? 0);
    } finally {
      setIsSaving(false);
    }
  };

  // Vérifier si la valeur a changé (comparer avec la dernière valeur sauvegardée)
  const hasChanged = localRepeatNewHifz !== lastSavedValue;
  
  // ✅ NOUVEAU - Vérifier si les évaluations ont changé
  const evaluationsChanged = 
    localEvalNewHifz !== lastSavedEvaluations.evalNewHifz ||
    localEvalOldHifzMurajah !== lastSavedEvaluations.evalOldHifzMurajah ||
    localEvalNewHifzMurajah !== lastSavedEvaluations.evalNewHifzMurajah;
  
  // ✅ NOUVEAU - Fonction pour sauvegarder les évaluations
  const handleSaveEvaluations = async () => {
    if (!latestRecord.id || !onUpdateEvaluations) return;
    
    setIsSavingEvaluations(true);
    try {
      // Préparer uniquement les valeurs modifiées
      const evaluationsToUpdate: { evalNewHifz?: string; evalOldHifzMurajah?: string; evalNewHifzMurajah?: string } = {};
      
      if (localEvalNewHifz !== lastSavedEvaluations.evalNewHifz) {
        evaluationsToUpdate.evalNewHifz = localEvalNewHifz;
      }
      if (localEvalOldHifzMurajah !== lastSavedEvaluations.evalOldHifzMurajah) {
        evaluationsToUpdate.evalOldHifzMurajah = localEvalOldHifzMurajah;
      }
      if (localEvalNewHifzMurajah !== lastSavedEvaluations.evalNewHifzMurajah) {
        evaluationsToUpdate.evalNewHifzMurajah = localEvalNewHifzMurajah;
      }
      
      await onUpdateEvaluations(latestRecord.id, evaluationsToUpdate);
      
      // ✅ NOUVEAU : Si evalNewHifz n'est plus positive, réinitialiser repeatNewHifz à 0
      const isPositiveEval = localEvalNewHifz === 'Excellent' || localEvalNewHifz === 'Good' || localEvalNewHifz === 'Moyen';
      const wasPositiveEval = lastSavedEvaluations.evalNewHifz === 'Excellent' || 
                              lastSavedEvaluations.evalNewHifz === 'Good' || 
                              lastSavedEvaluations.evalNewHifz === 'Moyen';
      
      // Si l'évaluation passe de positive à non positive ET que repeatNewHifz n'est pas déjà 0
      if (wasPositiveEval && !isPositiveEval && localRepeatNewHifz > 0 && latestRecord.studentId && onUpdateRepeatNewHifz) {
        console.log('⚠️ QuranRecordSection - Sauvegarde évaluations : Réinitialisation de repeatNewHifz car évaluation non positive:', {
          previousEval: lastSavedEvaluations.evalNewHifz,
          newEval: localEvalNewHifz,
          currentRepeatNewHifz: localRepeatNewHifz,
          studentId: latestRecord.studentId
        });
        
        // Réinitialiser localement
        setLocalRepeatNewHifz(0);
        setLastSavedValue(0);
        
        // Réinitialiser dans la base de données
        try {
          await onUpdateRepeatNewHifz(latestRecord.studentId, 0);
        } catch (repeatError) {
          console.error('❌ Erreur lors de la réinitialisation de repeatNewHifz:', repeatError);
        }
      }
      
      // Mettre à jour les dernières valeurs sauvegardées
      setLastSavedEvaluations({
        evalNewHifz: localEvalNewHifz,
        evalOldHifzMurajah: localEvalOldHifzMurajah,
        evalNewHifzMurajah: localEvalNewHifzMurajah
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des évaluations:', error);
      // Revenir aux anciennes valeurs en cas d'erreur
      setLocalEvalNewHifz(lastSavedEvaluations.evalNewHifz as any);
      setLocalEvalOldHifzMurajah(lastSavedEvaluations.evalOldHifzMurajah as any);
      setLocalEvalNewHifzMurajah(lastSavedEvaluations.evalNewHifzMurajah as any);
    } finally {
      setIsSavingEvaluations(false);
    }
  };

  // Utiliser les traductions centralisées de quranTracking
  const t = quranTrackingTranslations[language];
  
  const getText = (key: keyof typeof t) => {
    return t[key] || key;
  };

  const cleanTextValue = (text: string) => {
    return cleanText ? GLOBAL_UTILS.cleanText(text) : text;
  };

  // Utiliser la fonction centralisée depuis evaluationUtils

  // Fonction pour obtenir le style simple rouge/vert selon la condition
  const getSimpleRepeatBadgeStyle = (repeatNewHifz: number, targetRepeatNewHifzCount: number) => {
    if (targetRepeatNewHifzCount <= 0) {
      return {
        color: '#dc2626',
        backgroundColor: '#fee2e2'
      };
    }
    
    // Rouge si < target, vert si >= target
    if (repeatNewHifz < targetRepeatNewHifzCount) {
      return {
        color: '#dc2626',
        backgroundColor: '#fee2e2'
      };
    } else {
      return {
        color: '#166534',
        backgroundColor: '#dcfce7'
      };
    }
  };

  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 375; // Très petits écrans
  const useGridLayout = !isMobile;

  return (
    <div style={{
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      padding: '12px',
      marginTop: '16px'
    }}>
      <div style={{
        marginBottom: isMobile ? '10px' : '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <h4 style={{
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            fontSize: isMobile ? '14px' : '15px'
          }}>
            <span>{getText('lastSession')} - {GLOBAL_UTILS.formatDate(latestRecord.modifiedDate || '')}</span>
            {(() => {
              const dateString = latestRecord.modifiedDate || latestRecord.createdDate || '';
              if (!dateString) return null;
              
              // Utiliser la fonction globale pour formater l'heure
              const formattedTime = GLOBAL_UTILS.formatTime(dateString);
              if (!formattedTime) return null;
              
              // Vérifier si toutes les évaluations sont "غائب" (Absent)
              // Les valeurs sont en anglais dans la base de données, donc on vérifie 'Absent'
              // Normaliser les valeurs pour gérer les cas où elles pourraient être null, undefined, ou avoir des espaces
              const evalNewHifz = latestRecord.evalNewHifz ? String(latestRecord.evalNewHifz).trim() : null;
              const evalOldHifzMurajah = latestRecord.evalOldHifzMurajah ? String(latestRecord.evalOldHifzMurajah).trim() : null;
              const evalNewHifzMurajah = latestRecord.evalNewHifzMurajah ? String(latestRecord.evalNewHifzMurajah).trim() : null;
              const isAbsent = evalNewHifz === 'Absent' && evalOldHifzMurajah === 'Absent' && (!evalNewHifzMurajah || evalNewHifzMurajah === 'Absent' || evalNewHifzMurajah === 'NotDone');
              
              // Log désactivé pour réduire le bruit dans la console
              return (
                <>
                  <span style={{
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '400',
                    color: '#6b7280',
                    //fontFamily: 'monospace',
                    letterSpacing: '0.5px'
                  }}>
                    {formattedTime}
                  </span>
                      {isAbsent && (
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      ...getEvalBadgeStyle('Absent')
                    }}>
                      {getText('absent')}
                    </span>
                  )}
                </>
              );
            })()}
          </h4>
        </div>
        {canEdit && onEdit && (
          <button
            onClick={onEdit}
            style={{
              padding: '6px 12px',
              backgroundColor: '#9333ea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#7e22ce';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#9333ea';
            }}
          >
            ✏️ {getText('edit')}
          </button>
        )}
      </div>

      {/* Container avec 2 colonnes: آخر جلسة | الجلسة القادمة */}
      <div style={{
        display: useGridLayout ? 'grid' : 'block',
        gridTemplateColumns: useGridLayout ? '1fr 1fr' : '1fr',
        gap: isMobile ? '12px' : '16px',
        marginTop: isMobile ? '10px' : '12px'
      }}>
        
        {/* ========== COLONNE 1: آخر جلسة ========== */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: isSmallMobile ? '8px' : (isMobile ? '10px' : '12px'),
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            justifyContent: isSmallMobile ? 'flex-start' : 'space-between',
            alignItems: isSmallMobile ? 'flex-start' : 'center',
            margin: '0 0 10px 0',
            paddingBottom: '8px',
            borderBottom: '2px solid #e5e7eb',
            gap: isSmallMobile ? '8px' : '8px',
            flexWrap: 'wrap'
          }}>
            <h5 style={{
              fontSize: isSmallMobile ? '12px' : (isMobile ? '13px' : '14px'),
              fontWeight: '600',
              color: '#1f2937',
              margin: 0,
              width: isSmallMobile ? '100%' : 'auto'
            }}>
              {getText('lastSession')}
            </h5>
            
            {/* Boutons Annuler et Sauvegarder - uniquement si les évaluations ont changé */}
            {canEdit && onUpdateEvaluations && evaluationsChanged && (
              <div style={{
                display: 'flex',
                gap: isSmallMobile ? '4px' : '6px',
                alignItems: 'center',
                flexWrap: 'wrap',
                width: isSmallMobile ? '100%' : 'auto'
              }}>
                <button
                  onClick={() => {
                    // Annuler : restaurer les valeurs sauvegardées
                    setLocalEvalNewHifz(lastSavedEvaluations.evalNewHifz as any);
                    setLocalEvalOldHifzMurajah(lastSavedEvaluations.evalOldHifzMurajah as any);
                    setLocalEvalNewHifzMurajah(lastSavedEvaluations.evalNewHifzMurajah as any);
                  }}
                  disabled={isSavingEvaluations}
                  style={{
                    padding: isSmallMobile ? '3px 6px' : (isMobile ? '4px 8px' : '5px 10px'),
                    fontSize: isSmallMobile ? '10px' : (isMobile ? '11px' : '12px'),
                    fontWeight: '500',
                    color: '#6b7280',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: isSavingEvaluations ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    flex: isSmallMobile ? '1 1 auto' : 'none'
                  }}
                  onMouseOver={(e) => {
                    if (!isSavingEvaluations) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSavingEvaluations) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }
                  }}
                >
                  {getText('cancel')}
                </button>
                <button
                  onClick={handleSaveEvaluations}
                  disabled={isSavingEvaluations}
                  style={{
                    padding: isSmallMobile ? '3px 6px' : (isMobile ? '4px 8px' : '5px 10px'),
                    fontSize: isSmallMobile ? '10px' : (isMobile ? '11px' : '12px'),
                    fontWeight: '500',
                    color: '#ffffff',
                    backgroundColor: isSavingEvaluations ? '#9ca3af' : '#0369a1',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isSavingEvaluations ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    flex: isSmallMobile ? '1 1 auto' : 'none'
                  }}
                  onMouseOver={(e) => {
                    if (!isSavingEvaluations) {
                      e.currentTarget.style.backgroundColor = '#0284c7';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSavingEvaluations) {
                      e.currentTarget.style.backgroundColor = '#0369a1';
                    }
                  }}
                >
                  {isSavingEvaluations ? getText('saving') : `💾 ${getText('save')}`}
                </button>
              </div>
            )}
            
            <span style={{
              fontSize: isSmallMobile ? '10px' : (isMobile ? '11px' : '12px'),
              fontWeight: '600',
              color: '#6b7280',
              paddingRight: isSmallMobile ? '4px' : '8px',
              width: isSmallMobile ? '100%' : 'auto',
              marginTop: isSmallMobile ? '4px' : '0'
            }}>
              {getText('evaluation')}
            </span>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? '8px' : (isMobile ? '10px' : '12px') }}>
            {/* newHifzTxt: الحفظ الجديد - ✅ TOUJOURS AFFICHÉ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? '3px' : '4px' }}>
              {/* Badge - toujours pleine largeur */}
              {isMobile ? (
                <span style={{
                  fontSize: isSmallMobile ? '10px' : '11px',
                  fontWeight: '500',
                  color: '#166534',
                  backgroundColor: '#dcfce7',
                  padding: isSmallMobile ? '2px 4px' : '3px 6px',
                  borderRadius: '4px',
                  textAlign: 'center',
                  display: 'block',
                }}>
                  {getText('newHifzTxt')}
                </span>
              ) : null}
              {/* Ligne texte + dropdown (toujours côte à côte) */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: isSmallMobile ? '4px' : (isMobile ? '6px' : '8px'),
                flexWrap: 'nowrap',
              }}>
                {/* Badge inline sur desktop */}
                {!isMobile && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#166534',
                    backgroundColor: '#dcfce7',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {getText('newHifzTxt')}
                  </span>
                )}
                <span style={{
                  fontSize: isSmallMobile ? '12px' : (isMobile ? '13px' : '14px'),
                  color: latestRecord.newHifzTxt ? '#374151' : '#9ca3af',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}>
                  {cleanTextValue(latestRecord.newHifzTxt || '-')}
                </span>
                <div style={{ flexShrink: 0 }}>
                <select
                  value={localEvalNewHifz}
                  disabled={!canEdit || !onUpdateEvaluations || isSavingEvaluations}
                  onChange={(e) => handleEvalNewHifzChange(e.target.value as any)}
                  style={{
                    fontSize: isSmallMobile ? '10px' : '11px',
                    padding: isSmallMobile ? '2px 4px' : '2px 6px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    cursor: (!canEdit || !onUpdateEvaluations || isSavingEvaluations) ? 'not-allowed' : 'pointer',
                    width: 'auto',
                    minWidth: isSmallMobile ? '70px' : '75px',
                    ...getEvalBadgeStyle(localEvalNewHifz)
                  }}
                >
                  <option value="NotDone">{getText('evalNotDone')}</option>
                  <option value="Excellent">{getText('evalExcellent')}</option>
                  <option value="Good">{getText('evalGood')}</option>
                  <option value="Moyen">{getText('evalMoyen')}</option>
                  <option value="Repeat">{getText('evalRepeat')}</option>
                  <option value="Absent">{getText('evalAbsent')}</option>
                </select>
                </div>
              </div>
            </div>
            
            {/* oldHifzMurajahTxt: مراجعة الحفظ القديم - ✅ TOUJOURS AFFICHÉ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? '3px' : '4px' }}>
              {isMobile ? (
                <span style={{
                  fontSize: isSmallMobile ? '10px' : '11px',
                  fontWeight: '500',
                  color: '#1e40af',
                  backgroundColor: '#dbeafe',
                  padding: isSmallMobile ? '2px 4px' : '3px 6px',
                  borderRadius: '4px',
                  textAlign: 'center',
                  display: 'block',
                }}>
                  {getText('oldHifzMurajahTxt')}
                </span>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: isSmallMobile ? '4px' : (isMobile ? '6px' : '8px'), flexWrap: 'nowrap' }}>
                {!isMobile && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#1e40af',
                    backgroundColor: '#dbeafe',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {getText('oldHifzMurajahTxt')}
                  </span>
                )}
                <span style={{
                  fontSize: isSmallMobile ? '12px' : (isMobile ? '13px' : '14px'),
                  color: latestRecord.oldHifzMurajahTxt ? '#374151' : '#9ca3af',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}>
                  {cleanTextValue(latestRecord.oldHifzMurajahTxt || '-')}
                </span>
                <div style={{ flexShrink: 0, width: 'auto' }}>
                <select
                  value={localEvalOldHifzMurajah}
                  disabled={!canEdit || !onUpdateEvaluations || isSavingEvaluations}
                  onChange={(e) => handleEvalOldHifzMurajahChange(e.target.value as any)}
                  style={{
                    fontSize: isSmallMobile ? '10px' : '11px',
                    padding: isSmallMobile ? '2px 4px' : '2px 6px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    cursor: (!canEdit || !onUpdateEvaluations || isSavingEvaluations) ? 'not-allowed' : 'pointer',
                    width: 'auto',
                    minWidth: isSmallMobile ? '70px' : '75px',
                    ...getEvalBadgeStyle(localEvalOldHifzMurajah)
                  }}
                >
                  <option value="NotDone">{getText('evalNotDone')}</option>
                  <option value="Excellent">{getText('evalExcellent')}</option>
                  <option value="Good">{getText('evalGood')}</option>
                  <option value="Moyen">{getText('evalMoyen')}</option>
                  <option value="Repeat">{getText('evalRepeat')}</option>
                  <option value="Absent">{getText('evalAbsent')}</option>
                </select>
                </div>
              </div>
            </div>
            
            {/* newHifzMurajahText: مراجعة الحفظ القريب - ✅ TOUJOURS AFFICHÉ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? '3px' : '4px' }}>
              {isMobile ? (
                <span style={{
                  fontSize: isSmallMobile ? '10px' : '11px',
                  fontWeight: '500',
                  color: '#be185d',
                  backgroundColor: '#fce7f3',
                  padding: isSmallMobile ? '2px 4px' : '3px 6px',
                  borderRadius: '4px',
                  textAlign: 'center',
                  display: 'block',
                }}>
                  {getText('newHifzMurajahText')}
                </span>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: isSmallMobile ? '4px' : (isMobile ? '6px' : '8px'), flexWrap: 'nowrap' }}>
                {!isMobile && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#be185d',
                    backgroundColor: '#fce7f3',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {getText('newHifzMurajahText')}
                  </span>
                )}
                <span style={{
                  fontSize: isSmallMobile ? '12px' : (isMobile ? '13px' : '14px'),
                  color: latestRecord.newHifzMurajahText ? '#374151' : '#9ca3af',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}>
                  {cleanTextValue(latestRecord.newHifzMurajahText || '-')}
                </span>
                <div style={{ flexShrink: 0 }}>
                  <select
                    value={localEvalNewHifzMurajah}
                    disabled={!canEdit || !onUpdateEvaluations || isSavingEvaluations}
                    onChange={(e) => handleEvalNewHifzMurajahChange(e.target.value as any)}
                    style={{
                      fontSize: isSmallMobile ? '10px' : '11px',
                      padding: isSmallMobile ? '2px 4px' : '2px 6px',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      cursor: (!canEdit || !onUpdateEvaluations || isSavingEvaluations) ? 'not-allowed' : 'pointer',
                      width: 'auto',
                      minWidth: isSmallMobile ? '70px' : '75px',
                      ...getEvalBadgeStyle(localEvalNewHifzMurajah)
                    }}
                  >
                    <option value="NotDone">{getText('evalNotDone')}</option>
                    <option value="Excellent">{getText('evalExcellent')}</option>
                    <option value="Good">{getText('evalGood')}</option>
                    <option value="Moyen">{getText('evalMoyen')}</option>
                    <option value="Repeat">{getText('evalRepeat')}</option>
                    <option value="Absent">{getText('evalAbsent')}</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* تكرار آخر حفظ - ✅ NOUVEAU */}
            {lastPreviousRecord && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? '3px' : '4px' }}>
                {isMobile ? (
                  <span style={{
                    fontSize: isSmallMobile ? '10px' : '11px',
                    fontWeight: '500',
                    color: '#7c3aed',
                    backgroundColor: '#ede9fe',
                    padding: isSmallMobile ? '2px 4px' : '3px 6px',
                    borderRadius: '4px',
                    textAlign: 'center',
                    display: 'block',
                  }}>
                    {getText('repeatLastHifz')}
                  </span>
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: isSmallMobile ? '4px' : (isMobile ? '6px' : '8px'), flexWrap: 'nowrap' }}>
                  {!isMobile && (
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#7c3aed',
                      backgroundColor: '#ede9fe',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}>
                      {getText('repeatLastHifz')}
                    </span>
                  )}
                  <span style={{
                    fontSize: isSmallMobile ? '12px' : (isMobile ? '13px' : '14px'),
                    color: lastPreviousRecord.newHifzTxt ? '#374151' : '#9ca3af',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    minWidth: 0,
                    flex: '1 1 auto',
                  }}>
                    {cleanTextValue(lastPreviousRecord.newHifzTxt || '-')}
                  </span>
                  {lastPreviousRecord.repeatNewHifz !== undefined && (
                    <div style={{ flexShrink: 0 }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500',
                        ...getSimpleRepeatBadgeStyle(
                          lastPreviousRecord.repeatNewHifz,
                          targetRepeatNewHifzCount
                        )
                      }}>
                        {lastPreviousRecord.repeatNewHifz} {getText('times')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* الملاحظة - SEULEMENT texte personnalisé du prof */}
            {(() => {
              // Extraire le texte original de la note (sans les évaluations)
              let cleanedNote = latestRecord.note || '';
              
              if (extractOriginalNoteText && cleanedNote) {
                cleanedNote = extractOriginalNoteText(cleanedNote);
              } else if (cleanedNote) {
                // Fallback: supprimer "لم يتم" de la note
                cleanedNote = cleanedNote.replace(/لم يتم/g, '').trim();
              }
              
              // ✅ NOUVELLE LOGIQUE : Afficher SEULEMENT si le prof a écrit du texte
              const hasNote = cleanedNote && cleanedNote.trim() !== '';
              
              if (!hasNote) return null;
              
              return (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#92400e',
                    backgroundColor: '#fef3c7',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    flexShrink: 0
                  }}>
                    {getText('note')}:
                  </span>
                  
                  {/* ✅ SEULEMENT le texte personnalisé, PAS les badges d'évaluation */}
                  <span style={{
                    fontSize: '14px',
                    color: '#374151',
                    flex: 1
                  }}>
                    {cleanTextValue(cleanedNote)}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
        
        {/* ========== COLONNE 2: الجلسة القادمة ========== */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: isSmallMobile ? '8px' : (isMobile ? '10px' : '12px'),
          border: '1px solid #e5e7eb'
        }}>
          <h5 style={{
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 10px 0',
            paddingBottom: '8px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            {getText('nextSession')}
          </h5>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '12px' }}>
            {/* nextHalaqaNewHifzText: الحفظ للجلسة القادمة - ✅ TOUJOURS AFFICHÉ */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '8px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: '500',
                color: '#166534',
                backgroundColor: '#dcfce7',
                padding: isMobile ? '3px 6px' : '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                flexShrink: 0
              }}>
                {getText('nextHalaqaNewHifzText')}
              </span>
              <span style={{
                fontSize: isMobile ? '13px' : '14px',
                color: latestRecord.nextHalaqaNewHifzText ? '#374151' : '#9ca3af',
                flex: 1,
                wordBreak: 'break-word'
              }}>
                {cleanTextValue(latestRecord.nextHalaqaNewHifzText || '-')}
              </span>
            </div>
            
            {/* nextHalaqaOldHifzMurajahText: مراجعة الحفظ القديم للجلسة القادمة - ✅ TOUJOURS AFFICHÉ */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '8px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: '500',
                color: '#4338ca',
                backgroundColor: '#e0e7ff',
                padding: isMobile ? '3px 6px' : '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                flexShrink: 0
              }}>
                {getText('nextHalaqaOldHifzMurajahText')}
              </span>
              <span style={{
                fontSize: isMobile ? '13px' : '14px',
                color: latestRecord.nextHalaqaOldHifzMurajahText ? '#374151' : '#9ca3af',
                flex: 1,
                wordBreak: 'break-word'
              }}>
                {cleanTextValue(latestRecord.nextHalaqaOldHifzMurajahText || '-')}
              </span>
            </div>
            
            {/* nextHalaqaNewHifzMurajahText: مراجعة الحفظ القريب للجلسة القادمة - ✅ TOUJOURS AFFICHÉ */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '8px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: '500',
                color: '#be185d',
                backgroundColor: '#fce7f3',
                padding: isMobile ? '3px 6px' : '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                flexShrink: 0
              }}>
                {getText('nextHalaqaNewHifzMurajahText')}
              </span>
              <span style={{
                fontSize: isMobile ? '13px' : '14px',
                color: latestRecord.nextHalaqaNewHifzMurajahText ? '#374151' : '#9ca3af',
                flex: 1,
                wordBreak: 'break-word'
              }}>
                {cleanTextValue(latestRecord.nextHalaqaNewHifzMurajahText || '-')}
              </span>
            </div>
            
            {/* ✅ NOUVEAU: تكرار الحفظ الجديد (style harmonieux) */}
            {/* ✅ CONDITION : Afficher uniquement si évaluation positive (ممتاز, جيد, متوسط) */}
            {latestRecord.newHifzTxt && 
             latestRecord.newHifzTxt.trim() !== '' && 
             latestRecord.newHifzTxt !== '-' &&
             (localEvalNewHifz === 'Excellent' || localEvalNewHifz === 'Good' || localEvalNewHifz === 'Moyen') && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px',
                backgroundColor: '#ffffff',
                borderRadius: '6px'
              }}>
                {/* Ligne 1: Badge titre, texte, contrôles et sauvegarde */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {/* Badge titre */}
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#7c3aed',
                    backgroundColor: '#ede9fe',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    flexShrink: 0
                  }}>
                    {getText('repeatNewHifzLabel')}
                  </span>
                  
                  {/* Texte de la sélection */}
                  <span style={{
                    fontSize: '14px',
                    color: latestRecord.newHifzTxt ? '#374151' : '#9ca3af',
                    flex: 1
                  }}>
                    {cleanTextValue(latestRecord.newHifzTxt)}
                  </span>
                  
                  {/* Contrôles +/- - UNIQUEMENT POUR LES PARENTS */}
                  {typeof onUpdateRepeatNewHifz === 'function' && latestRecord.studentId && (
                    <>
                      {/* Contrôles +/- */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '2px'
                      }}>
                        <button
                          onClick={handleDecrease}
                          disabled={isSaving || localRepeatNewHifz <= 0}
                          style={{
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: localRepeatNewHifz <= 0 ? '#f3f4f6' : '#ffffff',
                            color: localRepeatNewHifz <= 0 ? '#9ca3af' : '#374151',
                            cursor: localRepeatNewHifz <= 0 || isSaving ? 'not-allowed' : 'pointer',
                            borderRadius: '4px',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (localRepeatNewHifz > 0 && !isSaving) {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (localRepeatNewHifz > 0 && !isSaving) {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }
                          }}
                        >
                          −
                        </button>
                        
                        <span style={{
                          minWidth: '40px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1f2937',
                          padding: '0 8px'
                        }}>
                          {localRepeatNewHifz}
                        </span>
                        
                        <button
                          onClick={handleIncrease}
                          disabled={isSaving}
                          style={{
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: '#ffffff',
                            color: '#374151',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            borderRadius: '4px',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (!isSaving) {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSaving) {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }
                          }}
                        >
                          +
                        </button>
                      </div>
                      
                      {/* Bouton Sauvegarder - Affiché uniquement si la valeur a changé */}
                      {hasChanged && (
                        <button
                          onClick={handleSave}
                          disabled={isSaving}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#ffffff',
                            backgroundColor: isSaving ? '#9ca3af' : '#0369a1',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => {
                            if (!isSaving) {
                              e.currentTarget.style.backgroundColor = '#0284c7';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSaving) {
                              e.currentTarget.style.backgroundColor = '#0369a1';
                            }
                          }}
                        >
                          {isSaving ? getText('savingEvals') : `💾 ${getText('save')}`}
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Indicateur objectif (15 مرة) - petit caractère indicatif */}
                  <span style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    fontStyle: 'italic',
                    flexShrink: 0
                  }}>
                    ({targetRepeatNewHifzCount} {getText('times')})
                  </span>
                </div>
                
                {/* Ligne 2: Message informatif - UNIQUEMENT POUR LES PARENTS */}
                {typeof onUpdateRepeatNewHifz === 'function' && latestRecord.studentId && (
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    fontStyle: 'italic',
                    padding: '4px 0',
                    lineHeight: '1.4'
                  }}>
{getText('mustRepeatPart1')} <b>({targetRepeatNewHifzCount} {getText('times')})</b> {getText('mustRepeatPart2')} </div>
                )}
              </div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
};

// Mémoriser le composant pour éviter les re-renders inutiles
const QuranRecordSection = React.memo(QuranRecordSectionComponent, (prevProps, nextProps): boolean => {
  // Comparaison personnalisée pour éviter les re-renders inutiles
  if (!prevProps.latestRecord || !nextProps.latestRecord) {
    return prevProps.latestRecord === nextProps.latestRecord;
  }
  
  // Comparer les propriétés importantes de latestRecord
  const prevRecord = prevProps.latestRecord;
  const nextRecord = nextProps.latestRecord;
  
  // Comparer lastPreviousRecord (important pour "تكرار آخر حفظ")
  const prevLastPrevious = prevProps.lastPreviousRecord;
  const nextLastPrevious = nextProps.lastPreviousRecord;
  
  // Si les deux sont null/undefined, ils sont égaux
  if (!prevLastPrevious && !nextLastPrevious) {
    // Continuer la comparaison des autres props
  } else if (!prevLastPrevious || !nextLastPrevious) {
    // L'un est null/undefined et l'autre non, donc différents
    return false;
  } else {
    // Les deux existent, comparer leurs propriétés
    // Utiliser la clé _key si elle existe, sinon comparer les valeurs
    if (prevLastPrevious._key && nextLastPrevious._key) {
      if (prevLastPrevious._key !== nextLastPrevious._key) {
        return false; // Les clés sont différentes, donc besoin de re-render
      }
    } else {
      // Fallback: comparer les valeurs directement
      if (prevLastPrevious.newHifzTxt !== nextLastPrevious.newHifzTxt ||
          prevLastPrevious.repeatNewHifz !== nextLastPrevious.repeatNewHifz) {
        // Les valeurs sont différentes, donc besoin de re-render
        return false;
      }
    }
  }
  
  return (
    prevRecord.id === nextRecord.id &&
    prevRecord.modifiedDate === nextRecord.modifiedDate &&
    prevRecord.createdDate === nextRecord.createdDate &&
    prevRecord.newHifzTxt === nextRecord.newHifzTxt &&
    prevRecord.oldHifzMurajahTxt === nextRecord.oldHifzMurajahTxt &&
    prevRecord.newHifzMurajahText === nextRecord.newHifzMurajahText &&
    prevRecord.nextHalaqaNewHifzText === nextRecord.nextHalaqaNewHifzText &&
    prevRecord.nextHalaqaOldHifzMurajahText === nextRecord.nextHalaqaOldHifzMurajahText &&
    prevRecord.nextHalaqaNewHifzMurajahText === nextRecord.nextHalaqaNewHifzMurajahText &&
    prevRecord.note === nextRecord.note &&
    prevRecord.evalNewHifz === nextRecord.evalNewHifz &&
    prevRecord.evalOldHifzMurajah === nextRecord.evalOldHifzMurajah &&
    prevRecord.evalNewHifzMurajah === nextRecord.evalNewHifzMurajah &&
    prevProps.canEdit === nextProps.canEdit &&
    prevProps.onUpdateEvaluations === nextProps.onUpdateEvaluations &&
    prevProps.windowWidth === nextProps.windowWidth &&
    prevProps.language === nextProps.language &&
    prevProps.cleanText === nextProps.cleanText &&
    prevProps.targetRepeatNewHifzCount === nextProps.targetRepeatNewHifzCount
    // Note: lastPreviousRecord est déjà comparé ci-dessus avec return false si différent
  );
});

export default QuranRecordSection;

